import React, { useState } from "react";
import { Layers, Zap } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { centsToAUD } from "../lib/events";

const Row = ({ value, label, muted, testId }) => (
  <div className={`flex items-baseline gap-2 ${muted ? "opacity-50" : ""}`} data-testid={testId}>
    <span className="font-heading text-lg font-black w-14 text-right shrink-0">{value}</span>
    <span className="text-sm">{label}</span>
  </div>
);

export const DemandFunnelCard = ({ conversion, crosssell, hasLinkedShows = true, eventId, variant = "full", nudgeActive = false }) => {
  const [creating, setCreating] = useState(false);
  const signups = conversion?.signups || 0;
  const notified = conversion?.notified || 0;
  const clicks = crosssell?.clicks_out || 0;
  const attributed = crosssell?.attributed_orders || 0;
  const revenue = crosssell?.attributed_net_revenue_cents || 0;
  const bp = crosssell?.by_placement || {};
  const hasActivity = signups > 0 || clicks > 0;

  const createSecondShow = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const dup = await api.post(`/events/${eventId}/second-show?from_waitlist_nudge=true`);
      window.location.href = `/dashboard/events/${dup.data.id}/edit`;
    } catch (err) {
      console.error("second-show create failed", err);
      toast.error("Couldn't create the second show. Please try again.");
      setCreating(false);
    }
  };

  if (!hasActivity) {
    if (variant === "overview") return null;
    return (
      <div className="text-sm text-[var(--gt-ink-3)] mb-4" data-testid="demand-funnel-empty">
        No demand activity yet.
      </div>
    );
  }

  return (
    <div className="border border-[var(--gt-line)] rounded-sm p-4 mb-4" data-testid={`demand-funnel-${variant}`}>
      <div className="flex items-center gap-2 mb-3">
        <Layers size={15} />
        <span className="font-heading text-sm font-black tracking-tight">Demand summary</span>
      </div>
      <div className="space-y-1.5">
        <Row value={signups} label={`waitlist signup${signups === 1 ? "" : "s"}`} testId="funnel-signups" />
        <Row value={notified} label="notified" muted={notified === 0} testId="funnel-notified" />
        {hasLinkedShows ? (
          <>
            <Row value={clicks} label="second-show clicks" muted={clicks === 0} testId="funnel-clicks" />
            <Row value={attributed} label={`attributed purchase${attributed === 1 ? "" : "s"}`} muted={attributed === 0} testId="funnel-attributed" />
            {attributed > 0 && (
              <Row value={centsToAUD(revenue)} label="attributed net revenue" testId="funnel-revenue" />
            )}
          </>
        ) : (
          <div className="flex items-baseline gap-2" data-testid="funnel-no-linked-shows">
            <span className="font-heading text-lg font-black w-14 text-right shrink-0">—</span>
            <span className="text-sm text-[var(--gt-ink-3)]">
              second-show clicks ·{" "}
              {nudgeActive && variant === "full" && eventId ? (
                <span className="inline-flex items-baseline gap-1.5" data-testid="funnel-high-demand-cta">
                  <span className="inline-flex items-center gap-1 font-semibold text-amber-700">
                    <Zap size={12} className="shrink-0" /> High demand detected.
                  </span>
                  <button
                    type="button"
                    disabled={creating}
                    onClick={createSecondShow}
                    className="font-semibold underline underline-offset-2 text-[var(--gt-ink-1)] disabled:opacity-50"
                    data-testid="funnel-create-second-show-btn"
                  >
                    {creating ? "Creating…" : "Create second show"}
                  </button>
                </span>
              ) : (
                <span className="italic">Create a second show to unlock cross-sell</span>
              )}
            </span>
          </div>
        )}
      </div>
      {hasLinkedShows && clicks > 0 && variant === "full" && (
        <div className="mt-2 text-xs text-[var(--gt-ink-3)]" data-testid="funnel-by-placement">
          Sold-out panel: {bp.event_panel || 0} · Section panel: {bp.section_panel || 0} · Date chips: {bp.also_playing || 0} · Waitlist email: {bp.waitlist_email || 0}
        </div>
      )}
      <div className="mt-3 pt-2 border-t border-[var(--gt-line)] text-[10px] leading-relaxed text-[var(--gt-ink-3)]">
        <span data-testid="funnel-signal-note">Shows related waitlist and second-show demand signals. Clicks may include public page and email activity.</span>
        {clicks > 0 && (
          <span data-testid="funnel-attribution-caveat">
            {" "}Estimated from consenting sessions only. Clicks include anonymous aggregate clicks; purchases are attributed only where consented session tracking is available.
          </span>
        )}
      </div>
    </div>
  );
};

export default DemandFunnelCard;
